import type { DriverFaceCluster } from '@cobliteam/common-types';
import { Carousel, type CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, Dialog, Image } from '@cobliteam/ui/components';
import { cn } from '@cobliteam/ui/utils';
import { AnimatePresence, motion } from 'motion/react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import DriverImageRemoveDialog from './DriverImageRemoveDialog';

interface DriverImagesCarouselProps {
  driverFaceCluster?: DriverFaceCluster;
  carouselImages?: string[];
  open?: boolean;
  children?: React.ReactNode;
  className?: string;
  showRemoveButton?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const DriverImagesCarousel = ({ driverFaceCluster, carouselImages, open, onOpenChange, children, showRemoveButton = true, className }: DriverImagesCarouselProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const [images, setImages] = useState<string[]>(carouselImages ?? []);

  const previewContainerRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const removeImage = (image: string) => {
    setImages(images.filter((img) => img !== image));
  };

  useEffect(() => {
    const container = previewContainerRef.current;
    if (container) setIsScrollable(container.scrollWidth > container.clientWidth);
  }, []);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();

    api.on('select', onSelect);

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  useEffect(() => {
    const container = previewContainerRef.current;
    const thumb = thumbnailRefs.current[current];

    if (!(container && thumb)) return;

    const containerRect = container.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const offset = thumbRect.left - containerRect.left - container.clientWidth / 2 + thumbRect.width / 2;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const newScrollLeft = Math.min(Math.max(container.scrollLeft + offset, 0), maxScrollLeft);

    container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
  }, [current]);

  useEffect(() => {
    if (driverFaceCluster) {
      setImages(driverFaceCluster.sampleImageUrls.map((image) => image.imageUrl));
    }
  }, [driverFaceCluster]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children && <Dialog.Trigger asChild>{children}</Dialog.Trigger>}

      <Dialog.Content size='lg'>
        <Dialog.Header>
          <Dialog.Title>Fotos do motorista</Dialog.Title>
          <Dialog.Close />
        </Dialog.Header>

        <Dialog.Body>
          <Carousel setApi={setApi} className='flex flex-col items-center gap-2'>
            <div className='relative'>
              <CarouselContent>
                <AnimatePresence mode='popLayout'>
                  {images.map((image, index) => (
                    <CarouselItem key={image}>
                      <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                        <Image src={image} alt={`Foto ${index + 1}`} className='max-h-[60vh] rounded-lg object-contain' />
                        {showRemoveButton && <DriverImageRemoveDialog imageUrl={image} removeImage={removeImage} />}
                      </motion.div>
                    </CarouselItem>
                  ))}
                </AnimatePresence>
              </CarouselContent>

              <CarouselPrevious color='default' className='-translate-y-1/2 absolute top-1/2 left-2 rounded-md bg-white' />
              <CarouselNext color='default' className='-translate-y-1/2 absolute top-1/2 right-1 rounded-md bg-white' />
            </div>

            <div ref={previewContainerRef} className='scrollbar-none flex w-full items-center overflow-x-auto'>
              <div className={cn('mx-auto flex items-center gap-2', isScrollable ? 'justify-start' : 'justify-center')}>
                {images.map((img, idx) => (
                  <motion.button
                    key={img}
                    ref={(el) => {
                      thumbnailRefs.current[idx] = el;
                    }}
                    type='button'
                    onClick={() => api?.scrollTo(idx)}
                    className={cn('size-16 shrink-0 overflow-hidden rounded-lg border-2 transition-colors', idx === current ? 'border-primary-11' : 'border-transparent')}
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{
                      opacity: idx === current ? 1 : 0.7,
                      scale: 1,
                    }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Image src={img} alt={`Foto ${idx + 1}`} className='object-cover' />
                  </motion.button>
                ))}
              </div>
            </div>
          </Carousel>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
};

export default DriverImagesCarousel;
